import { resolve } from "dns";
import {
  PathsRequirements,
  PoiRequirements,
  SnowmobileRequirements,
} from "../interfaces/api.interfaces";
import {
  PathCategoryResponse,
  PathLineStringResponse,
  PathResponse,
  SinglePathResponse,
} from "../interfaces/backend/path.interface";
import {
  PoiCategoryResponse,
  PoiResponse,
} from "../interfaces/backend/poi.interface";
import {
  SnowmobileFraResponse,
  SnowmobilePathResponse,
  SnowmobileSubPathResponse,
} from "../interfaces/backend/snowmobile-paths.interface";

export function getJson<T>(endpoint: string, useApiV2 = false): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const basePath = useApiV2
      ? process.env.REACT_APP_API_V2_URL
      : process.env.REACT_APP_API_URL;
    return fetch(`${basePath}${endpoint}`)
      .then((res) => res.json())
      .then((json) => resolve(json))
      .catch((error) => reject(error));
  });
}

/**
 * Fetches all the required data to render paths
 */
export function getPathData(category?: string): Promise<PathsRequirements> {
  return new Promise<PathsRequirements>((resolve, reject) => {
    Promise.all([
      getJson<PathResponse[]>(
        category ? `/paths?filter={"pathCategoryId":"${category}"}` : "/paths"
      ),
      getJson<PathCategoryResponse[]>("/paths/categories"),
      getJson<PathLineStringResponse[]>(
        category
          ? `/paths/linestring?filter={"pathCategoryId":"${category}"}`
          : "/paths/linestring",
        true
      ),
    ])
      .then(
        (
          responses: [PathResponse[], PathCategoryResponse[], PathLineStringResponse[]]
        ) => {
          const [paths, categories, gpsCoordinates] = responses;

          resolve({
            paths,
            categories,
            gpsCoordinates,
          });
        }
      )
      .catch((error) => reject(error));
  });
}

export function getSinglePathData(
  category?: string,
  id?: string
): Promise<SinglePathResponse> {
  return new Promise<SinglePathResponse>((resolve, reject) => {
    Promise.resolve(getJson<PathResponse>("/paths/" + id))
      .then((response: SinglePathResponse) => {
        const {
          name,
          name_en,
          number,
          length,
          difficulty,
          status,
          pathGpsCoordinates,
          description,
          description_en,
          start,
          end,
          pathCategoryId,
          tags,
          tagsRaw,
          pathCategory,
        } = response;

        resolve({
          name,
          name_en,
          number,
          length,
          difficulty,
          status,
          pathGpsCoordinates,
          description,
          description_en,
          start,
          end,
          pathCategoryId,
          tags,
          tagsRaw,
          pathCategory,
        });
      })
      .catch((error) => reject(error));
  });
}

export function getSingleSnowMobilePathData(
  id: string
): Promise<SnowmobilePathResponse> {
  return new Promise<SnowmobilePathResponse>((resolve, reject) => {
    Promise.resolve(getJson<SnowmobilePathResponse>("/snowmobile/trails/" + id))
      .then((response: SnowmobilePathResponse) => {
        const {
          id,
          name,
          name_en,
          description,
          description_en,
          length,
          medias,
          subPaths,
          returnSubPathIds,
          createdAt,
        } = response;

        resolve({
          id,
          name,
          name_en,
          description,
          description_en,
          length,
          medias,
          subPaths,
          returnSubPathIds,
          createdAt,
        });
      })
      .catch((error) => reject(error));
  });
}
export function getSingleSnowmobileSubPathData(
  id: string
): Promise<SnowmobileSubPathResponse> {
  return new Promise<SnowmobileSubPathResponse>((resolve, reject) => {
    Promise.resolve(
      getJson<SnowmobileSubPathResponse>("/snowmobile/subpaths/" + id)
    )
      .then((response: SnowmobileSubPathResponse) => {
        const {
          id,
          name,
          name_en,
          start,
          end,
          zone,
          status,
          areas,
          subPathGPSCoordinates,
          createAt,
          updatedAt,
          groomedAt,
        } = response;

        resolve({
          id,
          name,
          name_en,
          start,
          end,
          zone,
          status,
          areas,
          subPathGPSCoordinates,
          createAt,
          updatedAt,
          groomedAt,
        });
      })
      .catch((error) => reject(error));
  });
}

export function getSnowmobileFraData(): Promise<{
  fras: SnowmobileFraResponse[];
}> {
  return new Promise<{ fras: SnowmobileFraResponse[] }>((resolve, reject) => {
    Promise.resolve(getJson<SnowmobileFraResponse[]>("/snowmobile/fras/gps"))
      .then((response: SnowmobileFraResponse[]) => {
        const fras: SnowmobileFraResponse[] = response.map((item) => {
          const newFra: SnowmobileFraResponse = {
            id: item.id,
            name: item.name,
            fraGpsCoordinates: item.fraGpsCoordinates,
          };
          return newFra;
        });

        resolve({ fras: fras });
      })
      .catch((error) => reject(error));
  });
}

/**
 * Fetches all the required data to render snowmobile paths
 */
export function getSnowmobileData(): Promise<SnowmobileRequirements> {
  return new Promise<SnowmobileRequirements>((resolve, reject) => {
    Promise.all([
      getJson<SnowmobilePathResponse[]>("/snowmobile/trails"),
      getJson<SnowmobileSubPathResponse[]>("/snowmobile/subpaths/gps"),
      getJson<SnowmobileFraResponse[]>("/snowmobile/fras/gps"),
    ])
      .then(
        (
          responses: [
            SnowmobilePathResponse[],
            SnowmobileSubPathResponse[],
            SnowmobileFraResponse[]
          ]
        ) => {
          const [paths, subPaths, fras] = responses;

          resolve({
            paths,
            subPaths,
            fras,
          });
        }
      )
      .catch((error) => reject(error));
  });
}

/**
 * Fetches all the required data to render pois
 */
export function getPoiData(category?: string): Promise<PoiRequirements> {
  return new Promise<PoiRequirements>((resolve, reject) => {
    Promise.all([
      getJson<PoiResponse[]>(
        category
          ? `/pois?filter={"pointOfInterestCategoryId":${category}"}&range=[0,500]`
          : "/pois?range=[0,500]"
      ),
      getJson<PoiCategoryResponse[]>("/pois/categories"),
    ])
      .then((responses: [PoiResponse[], PoiCategoryResponse[]]) => {
        const [pois, categories] = responses;

        resolve({
          pois,
          categories,
        });
      })
      .catch((error) => reject(error));
  });
}

export function getPoiById(id: string): Promise<PoiResponse> {
  return new Promise<PoiResponse>((resolve, reject) => {
    Promise.resolve(getJson<PoiResponse>("/pois/" + id))
      .then((response: PoiResponse) => {
        resolve(response);
      })
      .catch((error) => reject(error));
  });
}
